
// Extend the VoiceConfig to support a plugins/customCommand system

/**
 * Command Plugin Interface
 * 
 * Defines a custom voice command plugin that can be registered
 * with the voice assistant. Plugins provide a flexible way to extend
 * the command system with application-specific functionality.
 * 
 * @example
 * ```
 * // Simple plugin example
 * const myPlugin: CommandPlugin = {
 *   triggers: "activate dark mode",
 *   action: (cmd) => toggleDarkMode(true),
 *   description: "Enables dark mode theme"
 * }
 * 
 * // Plugin with multiple triggers and condition
 * const advancedPlugin: CommandPlugin = {
 *   triggers: ["logout", "sign out"],
 *   action: (cmd, ctx) => logoutUser(),
 *   condition: (cmd, ctx) => isUserLoggedIn(),
 *   description: "Signs the current user out"
 * }
 * ```
 */
export interface CommandPlugin {
  /**
   * Command trigger phrases that activate this plugin.
   * Can be a single string or array of trigger phrases.
   */
  triggers: string | string[];
  
  /**
   * Function to execute when command is triggered.
   * @param command - The original command text
   * @param context - Optional context object with additional data
   */
  action: (command: string, context?: any) => void;
  
  /**
   * Alternative trigger phrases (aliases) for the command.
   */
  alias?: string[];
  
  /**
   * Optional condition function that determines if the plugin
   * should be activated. Useful for context-aware commands.
   * @param command - The original command text
   * @param context - Optional context object with additional data
   * @returns Boolean indicating if the command should execute
   */
  condition?: (command: string, context?: any) => boolean;
  
  /**
   * Human-readable description of what the command does.
   * Used for help text and debugging.
   */
  description?: string;
}

/**
 * Voice Configuration Interface
 * 
 * Central configuration for the voice command system.
 * Developers can customize behavior through this interface.
 */
export interface VoiceConfig {
  /**
   * Recognition language (e.g., 'en-US', 'es-ES')
   */
  language?: string;
  
  /**
   * Map of route names to URL paths.
   * Used by navigation commands.
   * @example { home: '/', settings: '/settings' }
   */
  routes?: Record<string, string>;
  
  /**
   * Legacy command handlers (simple dictionary approach).
   * @deprecated Use customCommandPlugins for new commands
   */
  commands?: Record<string, (command: string) => void>;
  
  /**
   * Plugin system for custom commands.
   * Recommended way to extend command functionality.
   */
  customCommandPlugins?: CommandPlugin[];
  
  /**
   * Customizable response text for different command types.
   */
  customResponses?: {
    navigate?: string;
    searchPrefix?: string;
    help?: string;
    notFound?: string;
  };
  
  /**
   * Alternative language setting
   * @deprecated Use language instead
   */
  lang?: string;
  
  /**
   * Current application path.
   * Used for context-aware commands.
   */
  currentPath?: string;
}

/**
 * Default configuration values
 */
export const defaultConfig: VoiceConfig = {
  language: 'en-US',
  routes: {
    'home': '/',
    'dashboard': '/dashboard',
    'settings': '/settings',
    'profile': '/profile',
  },
  customResponses: {
    navigate: 'Navigating to',
    searchPrefix: 'Searching for',
    help: "I can help you navigate the website. Try saying 'go to home' or 'search for accessibility'.",
    notFound: "I'm sorry, I didn't understand that command. Try asking for help to see what I can do.",
  }
};

/**
 * Extracts a page name from a voice command for navigation
 * @deprecated Use processCommand from voiceCommandUtils instead
 */
export const extractPageFromCommand = (command: string): string | null => {
  const goToMatch = command.match(/(?:go to|navigate to|open|show me)(?:\s+the)?\s+([a-z\s]+)(?:\s+page)?/i);
  
  if (goToMatch && goToMatch[1]) {
    return goToMatch[1].trim();
  }
  
  return null;
};

/**
 * Extracts a search query from a voice command
 * @deprecated Use processCommand from voiceCommandUtils instead
 */
export const extractSearchQuery = (command: string): string | null => {
  const searchMatch = command.match(/search(?:\s+for)?(?:\s+about)?\s+([a-z\s]+)/i);
  
  if (searchMatch && searchMatch[1]) {
    return searchMatch[1].trim();
  }
  
  return null;
};

/**
 * Checks if a domain matches an installation domain, including subdomains
 */
export const isDomainMatch = (refererDomain: string, installationDomain: string): boolean => {
  // Direct match
  if (refererDomain === installationDomain) return true;
  
  // Subdomain match (e.g., app.example.com matches example.com)
  if (refererDomain.endsWith('.' + installationDomain)) return true;
  
  // Development domains
  if (installationDomain === 'localhost' || 
      installationDomain === '127.0.0.1' ||
      installationDomain === '*') return true;
  
  // Special case for Webflow sites
  if (installationDomain.includes('webflow.io') && 
      refererDomain.includes('webflow.io')) return true;
  
  return false;
};
